/**
 * User-facing referral dashboard.
 * Shows the user's unique referral link, click/signup/conversion stats,
 * and a list of referred users with their status.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { referralsApi } from "@/api/referrals.ts";
import {
  PageContainer, TfCard, StatCard, Btn, Icon, TfSpinner,
} from "@/components/v2/primitives.tsx";

export function ReferralPage() {
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["referrals", "stats"],
    queryFn: () => referralsApi.stats(),
  });

  const copyLink = () => {
    if (!data?.referralUrl) return;
    navigator.clipboard.writeText(data.referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (isLoading) {
    return (
      <PageContainer title="Referrals">
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <TfSpinner size={32} />
        </div>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer title="Referrals">
        <TfCard padding={40}>
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Unable to load referral data</p>
            <p className="tf-muted" style={{ fontSize: 13 }}>
              {error instanceof Error ? error.message : "Please try again later."}
            </p>
          </div>
        </TfCard>
      </PageContainer>
    );
  }

  const stats = data;

  return (
    <PageContainer
      title="Referrals"
      subtitle="Share your link. When someone signs up and goes paid, you earn a free month."
    >
      {/* Referral link card */}
      <TfCard padding={24} style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <Icon name="share" size={20} />
          <span style={{ fontSize: 15, fontWeight: 600 }}>Your referral link</span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            readOnly
            value={stats.referralUrl}
            style={{
              flex: 1,
              padding: "10px 14px",
              fontSize: 14,
              fontFamily: "var(--tf-mono, monospace)",
              background: "var(--tf-surface)",
              border: "1px solid var(--tf-border)",
              borderRadius: 8,
              color: "var(--tf-fg)",
              outline: "none",
            }}
            onFocus={(e) => e.target.select()}
          />
          <Btn variant="primary" size="sm" onClick={copyLink}>
            <Icon name={copied ? "check" : "copy"} size={16} />
            {copied ? "Copied" : "Copy"}
          </Btn>
        </div>
        <p className="tf-muted" style={{ fontSize: 13, marginTop: 10 }}>
          Code: <strong>{stats.referralCode}</strong> · Cookie lasts 30 days · You earn 1 free month per paid conversion
        </p>
      </TfCard>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
        <StatCard label="Link clicks" value={stats.totalClicks} />
        <StatCard label="Signups" value={stats.totalSignups} />
        <StatCard label="Conversions" value={stats.totalConversions} />
        <StatCard label="Earned" value={`$${stats.totalEarned}`} />
      </div>

      {/* Referral history */}
      <TfCard padding={0}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--tf-border)" }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Referral history</span>
        </div>
        {stats.referrals.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <p className="tf-muted" style={{ fontSize: 14 }}>No referrals yet. Share your link to get started.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--tf-border)" }}>
                  <th style={{ textAlign: "left", padding: "10px 20px", fontWeight: 500, color: "var(--tf-fg-muted)" }}>User</th>
                  <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 500, color: "var(--tf-fg-muted)" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 500, color: "var(--tf-fg-muted)" }}>Signed up</th>
                  <th style={{ textAlign: "right", padding: "10px 20px", fontWeight: 500, color: "var(--tf-fg-muted)" }}>Reward</th>
                </tr>
              </thead>
              <tbody>
                {stats.referrals.map((r) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--tf-border)" }}>
                    <td style={{ padding: "10px 20px", fontFamily: "var(--tf-mono, monospace)" }}>{r.referredEmail}</td>
                    <td style={{ padding: "10px 16px" }}>
                      <span style={{
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "2px 8px",
                        borderRadius: 6,
                        background: r.status === "converted" ? "var(--tf-success-bg, #dcfce7)" : r.status === "signed_up" ? "var(--tf-info-bg, #dbeafe)" : "var(--tf-surface)",
                        color: r.status === "converted" ? "var(--tf-success, #16a34a)" : r.status === "signed_up" ? "var(--tf-info, #2563eb)" : "var(--tf-fg-muted)",
                      }}>
                        {r.status === "converted" ? "Paid" : r.status === "signed_up" ? "Free" : "Churned"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px", color: "var(--tf-fg-muted)" }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "10px 20px", textAlign: "right", fontWeight: 500 }}>
                      {r.status === "converted" ? `$${r.reward}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </TfCard>
    </PageContainer>
  );
}
